
import React, { useEffect, useMemo, useState } from 'react';
import { Card, ToggleSwitchField, TextField, Button, Spinner, Alert, Tooltip } from '@dopomogai/ui';
import { Bell, Brain, Lock } from 'lucide-react';
import { agentService, isApiError } from '@dopomogai/api-client';
import type { UUID, AgentNotificationSettings, AgentNotificationSettingsPayload } from '@dopomogai/types';
import { toast } from '@dopomogai/ui';

type AgentSettingsCardProps = {
  agentId: UUID;
  readOnly?: boolean;
};

type LocalForm = {
  summarizer_enabled: boolean;
  notify_on_conversation_started: boolean;
  notify_on_conversation_finished: boolean;
  notify_emails: string;
};

const normalizeEmails = (value: string) => {
  const list = Array.from(
    new Set(
      value
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
    )
  );
  return list;
};

export const AgentSettingsCard: React.FC<AgentSettingsCardProps> = ({ agentId, readOnly = false }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [form, setForm] = useState<LocalForm>({
    summarizer_enabled: false,
    notify_on_conversation_started: false,
    notify_on_conversation_finished: false,
    notify_emails: '',
  });
  const [initial, setInitial] = useState<LocalForm | null>(null);

  const dirty = useMemo(() => {
    if (!initial) return false;
    return (
      initial.summarizer_enabled !== form.summarizer_enabled ||
      initial.notify_on_conversation_started !== form.notify_on_conversation_started ||
      initial.notify_on_conversation_finished !== form.notify_on_conversation_finished ||
      initial.notify_emails !== form.notify_emails
    );
  }, [form, initial]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const res = await agentService.getAgentNotificationSettings(agentId);
        if (isApiError(res)) {
          setLoadError(res.message || 'Failed to load settings.');
        } else if (mounted) {
          const emails = (res.notify_emails || []).join(', ');
          const next: LocalForm = {
            summarizer_enabled: !!res.summarizer_enabled,
            notify_on_conversation_started: !!res.notify_on_conversation_started,
            notify_on_conversation_finished: !!res.notify_on_conversation_finished,
            notify_emails: emails,
          };
          setForm(next);
          setInitial(next);
        }
      } catch (e: any) {
        setLoadError(e?.message || 'Failed to load settings.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [agentId]);

  const onToggle = (key: keyof LocalForm) => (e: React.ChangeEvent<HTMLInputElement>) => {
    if (readOnly) return;
    setForm(prev => ({ ...prev, [key]: e.target.checked as any }));
  };
  const onEmailsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (readOnly) return;
    setForm(prev => ({ ...prev, notify_emails: e.target.value }));
  };

  const handleReset = () => {
    if (readOnly) return;
    if (initial) setForm(initial);
  };

  const handleSave = async () => {
    if (readOnly) return;
    const payload: AgentNotificationSettingsPayload = {
      summarizer_enabled: form.summarizer_enabled,
      notify_on_conversation_started: form.notify_on_conversation_started,
      notify_on_conversation_finished: form.notify_on_conversation_finished,
      notify_emails: normalizeEmails(form.notify_emails),
    };
    setSaving(true);
    try {
      const res = await agentService.updateAgentNotificationSettings(agentId, payload);
      if (isApiError(res)) {
        toast.error(res.message || 'Failed to save settings.');
        return;
      }
      const emails = (res.notify_emails || []).join(', ');
      const next: LocalForm = {
        summarizer_enabled: !!res.summarizer_enabled,
        notify_on_conversation_started: !!res.notify_on_conversation_started,
        notify_on_conversation_finished: !!res.notify_on_conversation_finished,
        notify_emails: emails,
      };
      setForm(next);
      setInitial(next);
      toast.success('Agent settings saved.');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      title="Agent settings"
      headerBgColor="bg-slate-100"
      className="border-2 border-black"
      actionButton={readOnly ? <Tooltip text="View-only"><Lock size={16} className="text-slate-500" /></Tooltip> : undefined}
    >
      {loading ? (
        <div className="py-10 flex items-center justify-center">
          <Spinner text="Loading settings…" />
        </div>
      ) : loadError ? (
        <Alert type="error" title="Failed to load" message={loadError} />
      ) : (
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-3 border-2 border-black rounded-lg bg-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Brain size={16} className="text-slate-700" />
                  <div>
                    <p className="font-semibold text-slate-900 text-sm">Summarizer</p>
                    <p className="text-xs text-slate-500">Automatically summarize new conversations</p>
                  </div>
                </div>
                <ToggleSwitchField
                  label="Summarizer"
                  labelPosition="hidden"
                  checked={form.summarizer_enabled}
                  onChange={onToggle('summarizer_enabled')}
                  disabled={readOnly}
                />
              </div>
            </div>
            <div className="p-3 border-2 border-black rounded-lg bg-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bell size={16} className="text-slate-700" />
                  <div>
                    <p className="font-semibold text-slate-900 text-sm">Notify on conversation start</p>
                    <p className="text-xs text-slate-500">Emails when a conversation begins</p>
                  </div>
                </div>
                <ToggleSwitchField
                  label="Notify start"
                  labelPosition="hidden"
                  checked={form.notify_on_conversation_started}
                  onChange={onToggle('notify_on_conversation_started')}
                  disabled={readOnly}
                />
              </div>
            </div>
            <div className="p-3 border-2 border-black rounded-lg bg-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bell size={16} className="text-slate-700" />
                  <div>
                    <p className="font-semibold text-slate-900 text-sm">Notify when summary is ready</p>
                    <p className="text-xs text-slate-500">Emails after summarizer completes</p>
                  </div>
                </div>
                <ToggleSwitchField
                  label="Notify summary done"
                  labelPosition="hidden"
                  checked={form.notify_on_conversation_finished}
                  onChange={onToggle('notify_on_conversation_finished')}
                  disabled={readOnly}
                />
              </div>
            </div>
            <div className="p-3 border-2 border-black rounded-lg bg-white">
              <div className="space-y-2">
                <p className="font-semibold text-slate-900 text-sm">Notification emails</p>
                <TextField
                  label="Emails (comma separated)"
                  value={form.notify_emails}
                  onChange={onEmailsChange}
                  placeholder="team@example.com, you@example.com"
                  disabled={readOnly}
                />
                <p className="text-xs text-slate-500">
                  Leave blank to notify only the agent owner.
                </p>
              </div>
            </div>
          </div>

          {!readOnly && (
            <div className="flex items-center justify-end gap-2">
              <Tooltip text="Revert changes to last saved">
                <Button
                  variant="outline"
                  text="Reset"
                  onClick={handleReset}
                  disabled={!dirty || saving}
                />
              </Tooltip>
              <Button
                variant="primary"
                text={saving ? 'Saving…' : 'Save settings'}
                onClick={handleSave}
                disabled={!dirty || saving}
              />
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

export default AgentSettingsCard;
